import type { WorkbookApi } from '@mathflat/domain/@entities/(Content)/Workbook/api'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import axios from 'axios'

import { mpEventInfo } from '@/constant'
import type { DefaultResponse } from '@/types/common'
import maxios, { type CustomRequestConfigMeta } from '@/utils/axios/maxios'

const customWorkbookPath = `/workbook/custom`

export const workbookApi = {
  customWorkbook: {
    getProblemCount: async <T extends WorkbookApi.CustomWorkbookType>(
      payload: WorkbookApi.GetProblemCount.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await maxios.post<WorkbookApi.GetProblemCount.Response<T>>(
        customWorkbookPath + '/problem-count',
        payload,
        { meta },
      )
      return data
    },
    getChapterProblemsByFilter: async <
      T extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'
        : never = never,
    >(
      payload: WorkbookApi.GetProblems.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await maxios.post<WorkbookApi.GetProblems.Response<T, U>>(
        customWorkbookPath + '/problem',
        payload,
        { meta },
      )

      return data
    },
    getChapterProblemsByWorkbookId: async <
      T extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'
        : never = never,
    >(
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await maxios.get<WorkbookApi.GetProblems.Response<T, U>>(
        customWorkbookPath + `/${workbookId}/problem`,
        { meta },
      )
      return data
    },
    getWorkbookById: async (
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      return maxios.get<WorkbookApi.GetCustomWorkbookById.Response>(
        customWorkbookPath + `/${workbookId}`,
        { meta },
      )
    },
    postWorkbook: async <
      T extends WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER' // CHAPTER일 경우 소단원인지 중단원인지
        : never = never,
    >(
      payload: WorkbookApi.PostPutCustomWorkbook.Request<T, U>,
      meta?: CustomRequestConfigMeta,
    ) => {
      return maxios.post<WorkbookApi.PostPutCustomWorkbook.Response>(customWorkbookPath, payload, {
        meta,
      })
    },
    putWorkbook: async <
      T extends WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER' // CHAPTER일 경우 소단원인지 중단원인지
        : never = never,
    >(
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      payload: WorkbookApi.PostPutCustomWorkbook.Request<T, U>,
      meta?: CustomRequestConfigMeta,
    ) => {
      return maxios.put<WorkbookApi.PostPutCustomWorkbook.Response>(
        customWorkbookPath + `/${workbookId}`,
        payload,
        {
          meta,
        },
      )
    },
    getWorkbookProblemById: async <T extends WorkbookApi.CustomWorkbookType>(
      workbookId: WorkbookApi.GetCustomWorkbookProblemById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      return maxios.get<WorkbookApi.GetCustomWorkbookProblemById.Response<T>>(
        customWorkbookPath + `/${workbookId}/problem`,
        { meta },
      )
    },
    getInnerDesignTemplate: async <T extends WorkbookApi.CustomWorkbookType>(
      payload: WorkbookApi.PostInnerDesignTemplate.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      return maxios.post<WorkbookApi.PostInnerDesignTemplate.Response[]>(
        customWorkbookPath + `/inner-design-template`,
        payload,
        { meta },
      )
    },
    deleteWorkbooks: (workbookIds: WorkbookEntity.CustomWorkbook['id'][]) => {
      return maxios({
        url: '/workbook',
        method: 'DELETE',
        data: workbookIds,
        params: { mp_event_name: mpEventInfo.교재삭제 },
      })
    },
  },

  supportedWorkbook: {
    getSupportedWorkbookPageList: (workbookId: WorkbookEntity.Workbook['id']) => {
      return maxios.get<WorkbookApi.GetSupportedWorkbookPageList.Response[]>(
        `/workbook/${workbookId}/page/v2`,
        {
          params: { mp_event_name: mpEventInfo.교재페이지조회 },
        },
      )
    },
  },

  getAssignedStudentList: (workbookId: WorkbookEntity.Workbook['id']) => {
    return maxios.get<DefaultResponse<res.AssignedStudentList[]>>(`/workbook/${workbookId}/assign`)
  },

  customSignatureWorkbook: {
    deleteSignatureWorkbook: (workbookId: WorkbookEntity.CustomSignatureWorkbook['id']) => {
      return maxios.delete(`v2/workbook/custom-signature/${workbookId}`)
    },
    postFilter: async (payload: WorkbookApi.CreateCustomSignatureWorkbookFilter.Request) => {
      const formData = new FormData()

      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          formData.append(key, typeof value === 'number' ? `${value}` : value)
        }
      })
      return maxios.post<WorkbookApi.CreateUpdateSignatureWorkbookFilter.Response>(
        `/v3/workbook/custom-signature/filter`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
    },
    getFilterByFilterId: async (
      filterId: WorkbookApi.CreateUpdateSignatureWorkbookFilter.Response['filterId'],
    ) => {
      return maxios.get<WorkbookApi.GetCustomSignatureWorkbookFilter.Response>(
        `/v3/workbook/custom-signature/filter`,
        { params: { filterId } },
      )
    },
    postCustomSignature: async (
      payload: WorkbookApi.CreateUpdateCustomSignatureWorkbook.Request,
    ) => {
      return maxios.post<WorkbookApi.PostPutCustomWorkbook.Response>(
        `/v3/workbook/custom-signature`,
        payload,
      )
    },
    putCustomSignature: async (
      id: WorkbookEntity.CustomSignatureWorkbook['id'],
      payload: WorkbookApi.CreateUpdateCustomSignatureWorkbook.Request,
    ) => {
      return maxios.put<WorkbookApi.PostPutCustomWorkbook.Response>(
        `/v3/workbook/custom-signature/${id}`,
        payload,
      )
    },
    restoreCustomSignatureWorkbook: (workbookId: WorkbookEntity.CustomSignatureWorkbook['id']) => {
      return maxios.post(`v2/workbook/custom-signature/${workbookId}/restore`)
    },
  },

  getWorkbookDownload: (params: Omit<req.WorkbookDownloadParamsV2, 'version'>) => {
    return maxios.get<common.PresignedFile>(`/workbook/v2/download`, {
      params: { ...params, version: 'v2', mp_event_name: mpEventInfo.교재다운로드 },
    })
  },

  getInspectWorkbooks() {
    return axios.get<res.InspectWorkbookList>(
      `https://download.${domainProvider.TARGET_DOMAIN}/certified-books.json`,
    )
  },
}
